import assignmentService from 'ApiService/Teacher/Assignment';
import Nodatafound from 'pages/Common/Nodatafound';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';
import EditAssignment from '../Model/EditAssignment';
import StudentUploadAssignment from './StudentUploadAssignment';
import ConfirmationModal from 'pages/Common/ConfirmationModal';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AllAssignment = ({ classData, setHidePrev }) => {
  const [assignment, setAssignment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [studentAssignmentLoading, setStudentAssignmentLoading] =
    useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmAction: () => {},
    icon: 'info',
  });

  const fetchAssignment = async () => {
    setLoading(true);

    try {
      const response = await assignmentService.getAllPracticalAssignments({
        batchId: classData.batchId,
      });

      if (response.data.success) {
        setAssignment(response.data.data);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch Assignment.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await assignmentService.deletePracticalAssignment(id);
      if (response.data.success) {
        toast.success('Assignment deleted successfully.');
        setAssignment((prev) => prev.filter((item) => item._id !== id));
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to delete Assignment.'
      );
    }
  };

  const handleUpdate = async (updatedAssignment) => {
    try {
      let response;

      if (updatedAssignment.newPdfFile) {
        const formData = new FormData();

        Object.keys(updatedAssignment).forEach((key) => {
          if (key === 'newPdfFile') {
            formData.append('pdfFile', updatedAssignment.newPdfFile);
          } else {
            formData.append(key, updatedAssignment[key]);
          }
        });

        response = await assignmentService.updatePracticalAssignments(
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      } else {
        response = await assignmentService.updatePracticalAssignments(
          updatedAssignment
        );
      }

      if (response.data.success) {
        // Show confirmation modal after successful update
        openConfirmationModal(
          'Update Successful',
          `Assignment "${updatedAssignment.title}" has been updated successfully.`,
          () => {
            closeConfirmationModal();
            setEditingAssignment(null);
            fetchAssignment();
          },
          'success'
        );
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to update Assignment.'
      );
    }
  };

  const openConfirmationModal = (
    title,
    message,
    confirmAction,
    icon = 'info'
  ) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      confirmAction,
      icon,
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal({
      ...confirmationModal,
      isOpen: false,
    });
  };

  const confirmDelete = (id, title) => {
    openConfirmationModal(
      'Confirm Delete',
      `Are you sure you want to delete the assignment "${title}"?`,
      () => {
        handleDelete(id);
        closeConfirmationModal();
      },
      'warning'
    );
  };

  const confirmEdit = (assignment) => {
    openConfirmationModal(
      'Confirm Edit',
      `Do you want to edit the assignment "${assignment.title}"?`,
      () => {
        setEditingAssignment(assignment);
        closeConfirmationModal();
      }
    );
  };

  const handleAssignClick = (assignment) => {
    openConfirmationModal(
      'Confirm Assignment',
      `Are you sure you want to assign "${assignment.title}" to batch "${classData.batchId}"?`,
      () => {
        handleAssignToBatch(assignment._id, classData.batchId);
        closeConfirmationModal();
      }
    );
  };

  const handleAssignToBatch = async (assignmentId, batchId) => {
    try {
      const response =
        await assignmentService.assignPracticalAssignmentToStudent({
          assignmentId,
          batchId: classData.batchId,
        });

      if (response.data.success) {
        toast.success('Assignment assigned successfully to the batch.');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to assign Assignment.'
      );
    }
  };

  const handleViewPdf = (e, filePath) => {
    e.stopPropagation();
    const pdfUrl = `${BASE_URL}/${filePath.replace(/\\/g, '/')}`;
    window.open(pdfUrl, '_blank');
  };

  const renderPdfViewer = (pdfFilePath) => {
    if (!pdfFilePath) return null;

    const pdfUrl = `${BASE_URL}/${pdfFilePath.replace(/\\/g, '/')}`;

    return (
      <div className="mt-4 rounded border border-gray-300 p-2">
        <iframe
          src={`${pdfUrl}#toolbar=0`}
          width="100%"
          height="300"
          className="rounded"
          title="Assignment PDF"
        />
      </div>
    );
  };

  useEffect(() => {
    fetchAssignment();
  }, [classData.batchId]);

  const handleAssignmentClick = (assignment) => {
    setHidePrev(true);
    setSelectedAssignment(assignment);
    setShowDetails(true);
    setStudentAssignmentLoading(true);
    setTimeout(() => {
      setStudentAssignmentLoading(false);
    }, 800);
  };

  const handleBackClick = () => {
    setShowDetails(false);
    setSelectedAssignment(null);
    setHidePrev(false);
  };

  return (
    <div>
      <div className="mt-10">
        {showDetails ? (
          <div className="flex flex-col items-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Selected Assignment: {selectedAssignment?.title}
            </h2>

            {selectedAssignment?.pdfFilePath && (
              <div className="mt-4 w-full max-w-3xl">
                <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                  Assignment PDF
                </h3>
                {renderPdfViewer(selectedAssignment.pdfFilePath)}
              </div>
            )}

            <StudentUploadAssignment selectedAssignment={selectedAssignment} />

            <div className="flex w-full justify-end px-3 pt-2 md:px-8">
              <button
                onClick={handleBackClick}
                className="mb-3 cursor-pointer rounded-xl bg-lightPrimary px-16 py-2 font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 dark:active:bg-white/20 md:mb-0"
              >
                Back
              </button>
            </div>
          </div>
        ) : loading ? (
          <div className="flex h-40 items-center justify-center">
            <BallTriangle color="#5431ed" />
          </div>
        ) : assignment.length === 0 ? (
          <div className="flex h-40 items-center justify-center dark:text-gray-600">
            <Nodatafound title="No Assignment found" />
          </div>
        ) : (
          <div className="grid h-full w-full grid-cols-1 gap-4 p-2 md:grid-cols-2 lg:grid-cols-3">
            {assignment.map((test) => (
              <div
                key={test._id}
                onClick={() => handleAssignmentClick(test)}
                className="cursor-pointer rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal text-gray-600 outline-none hover:border-brand-500 dark:border-[#323B5D]  dark:bg-navy-900 dark:text-white"
              >
                <div className="w-full p-4">
                  <h5 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
                    {test.title}
                  </h5>

                  {test.question && (
                    <p className="mb-2 font-normal text-gray-700 dark:text-gray-400">
                      Question: {test.question}
                    </p>
                  )}

                  {test.pdfFileName && (
                    <div className="mb-3">
                      <div className="flex items-center gap-2 font-normal text-gray-700 dark:text-gray-400">
                        <span>PDF File: {test.pdfFileName}</span>
                      </div>
                      <div className="mt-2">
                        <button
                          onClick={(e) => handleViewPdf(e, test.pdfFilePath)}
                          className="rounded bg-green-500 px-4 py-1 text-sm text-white"
                        >
                          View PDF
                        </button>
                      </div>
                      {/* Mini PDF preview */}
                      {test.pdfFilePath && (
                        <div
                          className="mt-2 h-16 w-full cursor-pointer overflow-hidden rounded border border-gray-300"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewPdf(e, test.pdfFilePath);
                          }}
                        >
                          <iframe
                            src={`${BASE_URL}/${test.pdfFilePath.replace(
                              /\\/g,
                              '/'
                            )}#toolbar=0`}
                            width="100%"
                            height="100"
                            className="pointer-events-none"
                            title={`PDF Preview ${test._id}`}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <p className="font-normal text-gray-700 dark:text-gray-400">
                    Created At:{' '}
                    {new Intl.DateTimeFormat('en-GB').format(
                      new Date(test.createdAt)
                    )}
                  </p>
                  <p className="font-normal text-gray-700 dark:text-gray-400">
                    Due Date:{' '}
                    {new Intl.DateTimeFormat('en-GB').format(
                      new Date(test.dueDate)
                    )}
                  </p>

                  <div className="mt-4 flex justify-between gap-2">
                    <button
                      className="rounded bg-blue-500 px-4 py-2 text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmEdit(test);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="rounded bg-red-500 px-4 py-2 text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDelete(test._id, test.title);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Modal */}
        {editingAssignment && (
          <EditAssignment
            assignment={editingAssignment}
            onClose={() => setEditingAssignment(null)}
            onSave={handleUpdate}
          />
        )}

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={confirmationModal.isOpen}
          onClose={closeConfirmationModal}
          onConfirm={confirmationModal.confirmAction}
          title={confirmationModal.title}
          message={confirmationModal.message}
          confirmText={
            confirmationModal.icon === 'warning'
              ? 'Yes, Delete'
              : confirmationModal.icon === 'success'
              ? 'OK'
              : 'Yes, Proceed'
          }
          cancelText={confirmationModal.icon === 'success' ? null : 'Cancel'}
          icon={confirmationModal.icon}
        />
      </div>
    </div>
  );
};

export default AllAssignment;
